import { useState } from 'react';
import { KeyRound, ListChecks, Sparkles, Trophy, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  useAddSeoKeywords,
  useRunSeoKeywords,
  useSeoKeywordIdeasQuery,
  useSeoKeywordsQuery,
  useTrackSeoKeyword,
  type ApiSeoKeyword,
  type SeoSite,
} from '@/client/api/marketing/seo';
import { EmptyState } from './components/marketing-ui';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  SITE_COLOR,
  SeoError,
  SeoKpi,
  SeoKpiRow,
  SeoNote,
  SeoPanel,
  ShareBar,
  StatusPill,
  TD,
  TH,
  TR,
} from './components/seo-ui';

const SITE_LABEL: Record<SeoSite, string> = { feelyfeely: 'FeelyFeely', jiji: 'Jiji', meqasa: 'Meqasa' };

const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString());

/** A position, with how far it moved since the previous check (lower is better). */
function Position({ now, before }: { now: number | null | undefined; before?: number | null }) {
  if (now == null) return <span className="whitespace-nowrap text-xs text-gray-400">Not in top 100</span>;
  const moved = before != null ? before - now : 0;
  return (
    <span className={cn('whitespace-nowrap tabular-nums', now <= 10 ? 'font-semibold text-gray-900' : 'text-gray-600')}>
      {now}
      {moved !== 0 && (
        <span className={cn('ml-1 text-xs font-normal', moved > 0 ? 'text-emerald-700' : 'text-rose-700')}>
          {moved > 0 ? `up ${moved}` : `down ${-moved}`}
        </span>
      )}
    </span>
  );
}

function Row({ kw, onTrack, busy }: { kw: ApiSeoKeyword; onTrack: (tracked: boolean) => void; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const hasMore = (kw.questions?.length ?? 0) + (kw.related?.length ?? 0) > 0;
  return (
    <>
      <tr className={cn(TR, 'align-top')}>
        <td className={TD}>
          <button
            type="button"
            onClick={() => hasMore && setOpen((v) => !v)}
            className={cn('text-left text-gray-900', hasMore && 'hover:underline')}
          >
            {kw.keyword}
          </button>
          {kw.url && <div className="max-w-xs truncate text-xs text-gray-500">{kw.url.replace(/^https?:\/\/[^/]+/, '')}</div>}
        </td>
        <td className={cn(TD, 'text-right tabular-nums')}>{fmt(kw.volume)}</td>
        <td className={cn(TD, 'text-right')}>
          {kw.positions ? <Position now={kw.positions.feelyfeely} before={kw.previous?.feelyfeely} /> : <span className="text-gray-400">—</span>}
        </td>
        <td className={cn(TD, 'text-right')}>
          {kw.positions ? <Position now={kw.positions.jiji} before={kw.previous?.jiji} /> : <span className="text-gray-400">—</span>}
        </td>
        <td className={cn(TD, 'text-right')}>
          {kw.positions ? <Position now={kw.positions.meqasa} before={kw.previous?.meqasa} /> : <span className="text-gray-400">—</span>}
        </td>
        <td className={cn(TD, 'max-w-[14rem] truncate text-gray-600')} title={kw.leader?.title}>
          {kw.leader?.domain ?? '—'}
        </td>
        <td className={TD}>
          {kw.ai_overview?.present ? (
            <StatusPill status={kw.ai_overview.cites_us ? 'pass' : 'warn'} label={kw.ai_overview.cites_us ? 'Cites us' : 'Shown'} />
          ) : (
            <span className="text-xs text-gray-400">{kw.positions ? 'None' : '—'}</span>
          )}
        </td>
        <td className={cn(TD, 'text-right')}>
          <button
            type="button"
            disabled={busy}
            onClick={() => onTrack(!kw.tracked)}
            className="whitespace-nowrap text-xs font-medium text-gray-600 hover:text-gray-900 hover:underline disabled:opacity-50"
          >
            {kw.tracked ? 'Stop tracking' : 'Track'}
          </button>
        </td>
      </tr>
      {open && (
        <tr className={cn(TR, 'bg-gray-50')}>
          <td colSpan={8} className="px-4 py-3 text-xs leading-relaxed text-gray-700">
            {(kw.questions?.length ?? 0) > 0 && (
              <p>
                <span className="font-medium">People also ask:</span> {kw.questions?.join(' · ')}
              </p>
            )}
            {(kw.related?.length ?? 0) > 0 && (
              <p className="mt-1">
                <span className="font-medium">Related searches:</span> {kw.related?.join(' · ')}
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** Seed in, related Ghana searches out; pick some and add them to the list. */
function KeywordIdeas({ onAdd, adding }: { onAdd: (keywords: string[]) => void; adding: boolean }) {
  const [draft, setDraft] = useState('');
  const [seed, setSeed] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const { data, isFetching, error } = useSeoKeywordIdeasQuery(seed);

  const find = () => {
    const s = draft.trim().toLowerCase();
    if (s) {
      setSeed(s);
      setPicked(new Set());
    }
  };
  const toggle = (kw: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(kw)) next.delete(kw);
      else next.add(kw);
      return next;
    });

  return (
    <SeoPanel
      title="Find keyword ideas"
      subtitle="Related searches in Ghana for a seed keyword. Each new seed is a paid lookup (about $0.02); the same seed again today is free."
      flush
      action={
        data && (
          <span className="text-xs text-gray-500">
            {data.lookups_today} of {data.lookups_per_day} lookups used today
          </span>
        )
      }
    >
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && find()}
          placeholder="fridge"
          className="min-w-[12rem] flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          aria-label="Seed keyword"
        />
        <Button size="sm" variant="outline" onClick={find} disabled={!draft.trim() || isFetching}>
          {isFetching ? 'Looking up…' : 'Find ideas'}
        </Button>
        {picked.size > 0 && (
          <Button
            size="sm"
            disabled={adding}
            onClick={() => {
              onAdd([...picked]);
              setPicked(new Set());
            }}
          >
            Add {picked.size} to the list
          </Button>
        )}
      </div>
      {error && (
        <div className="px-4 pb-3">
          <SeoError>{getApiErrorMessage(error, 'The lookup did not go through.')}</SeoError>
        </div>
      )}
      {data && data.ideas.length === 0 && <p className="px-4 pb-3 text-sm text-gray-500">No ideas for "{data.seed}".</p>}
      {data && data.ideas.length > 0 && (
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className={TH} />
                <th className={TH}>Keyword</th>
                <th className={cn(TH, 'text-right')}>Searches / month</th>
                <th className={cn(TH, 'text-right')}>Difficulty</th>
                <th className={TH}>Competition</th>
                <th className={TH}>Intent</th>
              </tr>
            </thead>
            <tbody>
              {data.ideas.map((idea) => (
                <tr key={idea.keyword} className={TR}>
                  <td className={cn(TD, 'w-8')}>
                    <input
                      type="checkbox"
                      aria-label={`Pick ${idea.keyword}`}
                      disabled={idea.in_list}
                      checked={idea.in_list || picked.has(idea.keyword)}
                      onChange={() => toggle(idea.keyword)}
                    />
                  </td>
                  <td className={cn(TD, 'text-gray-900')}>
                    {idea.keyword}
                    {idea.in_list && <span className="ml-2 text-xs text-gray-400">in the list</span>}
                  </td>
                  <td className={cn(TD, 'text-right tabular-nums')}>{fmt(idea.volume)}</td>
                  <td className={cn(TD, 'text-right tabular-nums')}>{fmt(idea.difficulty)}</td>
                  <td className={cn(TD, 'text-gray-600')}>{idea.competition?.toLowerCase() ?? '—'}</td>
                  <td className={cn(TD, 'text-gray-600')}>{idea.intent ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SeoPanel>
  );
}

/**
 * `/admin/seo/keywords` — the searches we want to win: how many people make
 * them, and where we, Jiji and Meqasa rank (ff-api service/seo_keywords.py).
 */
export default function SeoKeywordsPage() {
  const { data, isLoading, error } = useSeoKeywordsQuery();
  const add = useAddSeoKeywords();
  const track = useTrackSeoKeyword();
  const run = useRunSeoKeywords();
  const [draft, setDraft] = useState('');
  const [onlyTracked, setOnlyTracked] = useState(false);

  const summary = data?.summary;
  const rows = (data?.keywords ?? []).filter((k) => !onlyTracked || k.tracked);
  const share = (site: SeoSite) => (summary ? `${summary.top10[site]} of ${summary.checked}` : undefined);

  const submit = () => {
    const keywords = draft
      .split(/[\n,]/)
      .map((k) => k.trim())
      .filter(Boolean);
    if (keywords.length === 0) return;
    add.mutate({ keywords }, { onSuccess: () => setDraft('') });
  };

  return (
    <MarketingPageShell
      title="Keywords"
      description="The searches we want to win in Ghana: how many people make them each month, and where we, Jiji and Meqasa rank."
      crumbs={[{ label: 'SEO', href: '/admin/seo' }, { label: 'Keywords' }]}
      icon={KeyRound}
      actions={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => add.mutate({ seed: true })} disabled={add.isPending}>
            Build list from competitor supply
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => run.mutate({})}
            disabled={run.isPending || run.isSuccess || !summary?.tracked}
          >
            {run.isSuccess ? 'Queued: results in about 4 minutes' : 'Check rankings now'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <SeoError>Could not load keywords.</SeoError>}
        {(add.isError || track.isError || run.isError) && <SeoError>That did not go through. Try again.</SeoError>}
        {add.isSuccess && add.data?.data && (
          <p className="text-sm text-gray-600">
            Added {add.data.data.added} keyword{add.data.data.added === 1 ? '' : 's'}. Tracking{' '}
            {add.data.data.tracked} of {add.data.data.max_tracked}.
          </p>
        )}

        <SeoKpiRow>
          <SeoKpi
            label="Keywords"
            value={summary?.keywords}
            icon={ListChecks}
            hint={summary ? `${summary.tracked} tracked, ${summary.checked} checked` : undefined}
            loading={isLoading}
          />
          <SeoKpi
            label="Searches per month"
            value={summary?.monthly_searches}
            icon={TrendingUp}
            hint="Across the list, Ghana"
            loading={isLoading}
          />
          <SeoKpi
            label="We are in the top 10"
            value={share('feelyfeely')}
            icon={Trophy}
            tone={summary && summary.checked > 0 && summary.top10.feelyfeely === 0 ? 'bad' : 'neutral'}
            hint="Of keywords checked"
            loading={isLoading}
          />
          <SeoKpi
            label="AI overview shown"
            value={summary ? `${summary.ai_overviews} of ${summary.checked}` : undefined}
            icon={Sparkles}
            hint={summary ? `Cites us on ${summary.ai_cites_us}` : undefined}
            loading={isLoading}
          />
        </SeoKpiRow>

        {summary && summary.checked > 0 && (
          <SeoPanel
            title="First-page share"
            subtitle={`Share of the ${summary.checked} checked keywords where each site is in Google's top 10`}
          >
            {(data?.sites ?? []).map((site) => (
              <ShareBar
                key={site}
                label={SITE_LABEL[site]}
                value={Math.round((summary.top10[site] / summary.checked) * 100)}
                color={SITE_COLOR[site]}
                emphasis={site === 'feelyfeely'}
              />
            ))}
          </SeoPanel>
        )}

        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <label className="flex-1 text-sm text-gray-700">
            Add keywords, one per line or separated by commas
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              placeholder="iphone 15 price in ghana"
              className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            />
          </label>
          <Button size="sm" onClick={submit} disabled={add.isPending || !draft.trim()}>
            Add
          </Button>
        </div>

        <KeywordIdeas onAdd={(keywords) => add.mutate({ keywords })} adding={add.isPending} />

        {!isLoading && (data?.keywords.length ?? 0) === 0 ? (
          <EmptyState label="No keywords yet. Build the list from competitor supply, or add your own above." />
        ) : (
          <SeoPanel
            title="Keywords"
            subtitle="Largest first. Click a keyword for the questions and searches Google relates to it."
            flush
            action={
              <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input type="checkbox" checked={onlyTracked} onChange={(e) => setOnlyTracked(e.target.checked)} />
                Tracked only
              </label>
            }
          >
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className={TH}>Keyword</th>
                  <th className={cn(TH, 'text-right')}>Searches / month</th>
                  {(['feelyfeely', 'jiji', 'meqasa'] as SeoSite[]).map((s) => (
                    <th key={s} className={cn(TH, 'text-right')}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: SITE_COLOR[s] }} />
                        {SITE_LABEL[s]}
                      </span>
                    </th>
                  ))}
                  <th className={TH}>Ranks first</th>
                  <th className={TH}>AI overview</th>
                  <th className={TH} />
                </tr>
              </thead>
              <tbody>
                {rows.map((kw) => (
                  <Row
                    key={kw.keyword}
                    kw={kw}
                    busy={track.isPending}
                    onTrack={(tracked) => track.mutate({ keyword: kw.keyword, tracked })}
                  />
                ))}
              </tbody>
            </table>
          </SeoPanel>
        )}

        <SeoNote>
          Positions are Google results for Ghana, checked weekly, first 100 results. Search volume is Google's own
          monthly figure and is blank where Google reports none. Each ranking check is a paid lookup, so at most 60
          keywords are tracked.
        </SeoNote>
      </div>
    </MarketingPageShell>
  );
}
